import { usePortalActivityDownload } from '@/components/member-portal/usePortalActivityDownload'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { RegistrationComparison } from '@/lib/registration-comparison'
import {
  registrationQuery,
  type RegistrationQueryControls,
} from '@/lib/registration-comparison-query'

const changes = [
  { value: 'up', label: 'Up' },
  { value: 'down', label: 'Down' },
  { value: 'level', label: 'Level' },
  { value: 'new_this_year', label: 'New this year' },
  { value: 'not_yet_back', label: 'Not yet back' },
]

export function RegistrationToolbar({
  report,
  params,
  update,
}: {
  report: RegistrationComparison
} & RegistrationQueryControls) {
  const filter = (key: string) => ({
    selected: new Set((params.get(key) ?? '').split(',').filter(Boolean)),
    onChange: (values: Set<string>) => update(key, [...values].join(',')),
  })
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Search organizations"
          placeholder="Search organizations"
          className="w-64"
          value={params.get('q') ?? ''}
          onChange={(event) => update('q', event.target.value)}
        />
        {report.previous_edition === 'ok' && (
          <FilterDropdown
            label="Change"
            emptyLabel="Change"
            options={changes}
            {...filter('change')}
          />
        )}
        <FilterDropdown
          label="Member"
          emptyLabel="Member"
          options={[
            { value: 'member', label: 'Member' },
            { value: 'non-member', label: 'Non-member' },
          ]}
          {...filter('member')}
        />
        <FilterDropdown
          label="Membership level"
          emptyLabel="Membership level"
          options={report.membership_levels.map((level) => ({
            value: level.id,
            label: level.name,
          }))}
          {...filter('membership_level_id')}
        />
      </div>
      <RegistrationExport eventId={report.event.id} params={params} />
    </div>
  )
}

function RegistrationExport({ eventId, params }: { eventId: string; params: URLSearchParams }) {
  const base = `/api/v1/events/${encodeURIComponent(eventId)}/registration_comparison`
  const query = registrationQuery(params, true)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline">
          <Download className="mr-2 size-4" strokeWidth={1.5} />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <RegistrationDownload href={`${base}.csv?${query}`} filename="event-organizations.csv">
          CSV by organization
        </RegistrationDownload>
        <RegistrationDownload href={`${base}/places.csv?${query}`} filename="event-places.csv">
          CSV by place
        </RegistrationDownload>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function RegistrationDownload({
  href,
  filename,
  children,
}: {
  href: string
  filename: string
  children: React.ReactNode
}) {
  const action = usePortalActivityDownload(href, filename)
  return (
    <DropdownMenuItem asChild onSelect={(event) => event.preventDefault()}>
      <a
        href={href}
        download={filename}
        aria-disabled={action.pending}
        onClick={(event) => {
          event.preventDefault()
          if (!action.pending) void action.download()
        }}
      >
        {action.error ? 'Download failed, try again' : children}
      </a>
    </DropdownMenuItem>
  )
}
